import { useEffect, useState } from "react";
import { fetchCareerTimeline } from "../lib/api";
import { CheckCircle2, Circle, Target, Lock, ArrowRight, TrendingUp } from "lucide-react";

/**
 * CareerStepTimeline — Horizontal visual: past promotions → current (highlighted) → next step (with readiness bar) → future locked.
 */
export default function CareerStepTimeline({ employeeId, onPickNext }) {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetchCareerTimeline(employeeId).then(setTimeline).finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="card-surface p-6 text-sm text-slate-400" data-testid="career-timeline-loading">Zaman çizelgesi yükleniyor…</div>;
  const steps = timeline?.steps || [];
  if (steps.length === 0) return null;

  return (
    <div className="card-surface p-6" data-testid="career-timeline">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5 mb-1"><TrendingUp className="w-3.5 h-3.5" /> Kariyer Basamağım</div>
          <h3 className="font-display text-xl font-semibold">Şu an neredeyim, neye ne kadar kaldı?</h3>
        </div>
      </div>

      <div className="relative">
        <div className="absolute top-6 left-0 right-0 h-0.5 bg-gradient-to-r from-slate-200 via-emerald-400 to-slate-200" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative">
          {steps.map((step, i) => (
            <StepNode key={i} step={step} idx={i} onPick={onPickNext} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StepNode({ step, idx, onPick }) {
  const kind = step.kind;
  const cfg = {
    past: { color: "slate", icon: CheckCircle2, label: "Geçmiş", iconClass: "text-slate-400", ringClass: "ring-slate-200 bg-slate-50" },
    current: { color: "emerald", icon: Circle, label: "Şu an", iconClass: "text-emerald-600 fill-emerald-100", ringClass: "ring-emerald-400 bg-emerald-50 shadow-md" },
    next: { color: "sky", icon: ArrowRight, label: "Sıradaki", iconClass: "text-sky-600", ringClass: "ring-sky-300 bg-sky-50" },
    future: { color: "slate", icon: Lock, label: "İleride", iconClass: "text-slate-300", ringClass: "ring-slate-100 bg-slate-25 opacity-70" },
  }[kind] || { color: "slate", icon: Circle, label: kind, iconClass: "text-slate-400", ringClass: "ring-slate-200 bg-slate-50" };
  const Icon = cfg.icon;
  const ready = step.readiness_pct;
  return (
    <div className={`relative flex flex-col items-center text-center pt-2`} data-testid={`timeline-step-${idx}-${kind}`}>
      <div className={`w-12 h-12 rounded-full ring-4 ${cfg.ringClass} flex items-center justify-center z-10 mb-3`}>
        <Icon className={`w-5 h-5 ${cfg.iconClass}`} />
        {step.is_pinned && <Target className="absolute -top-1 -right-1 w-4 h-4 text-amber-500" />}
      </div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{cfg.label}</div>
      <div className="text-sm font-semibold text-slate-800 line-clamp-2 min-h-[2.5rem] px-2">{step.role_title || "—"}</div>
      {step.tenure_years != null && (
        <div className="text-[11px] text-slate-500 mt-1">{step.tenure_years} yıl</div>
      )}
      {ready != null && (
        <div className="w-full mt-2 px-1">
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-sky-500" style={{ width: `${ready}%` }} />
          </div>
          <div className="text-[11px] text-slate-600 mt-1">
            <span className="font-semibold text-sky-700">%{ready} hazır</span>
            <span className="text-slate-400"> · {step.criteria_met}/{step.criteria_total} kriter</span>
          </div>
          {onPick && !step.is_pinned && (
            <button type="button" onClick={() => onPick(step)} className="mt-2 text-[11px] text-sky-700 hover:underline" data-testid={`timeline-pin-${idx}`}>
              🎯 Bunu hedef yap
            </button>
          )}
        </div>
      )}
    </div>
  );
}
